import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import './SetupAgentPage.css';
import './ViewAgentPage.css';
import { 
  getAvailableEnvironments, 
  getSelectedEnvironment, 
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';

const QueuePerformanceReportPage = () => {
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [loadingQueues, setLoadingQueues] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());
  const availableEnvironments = getAvailableEnvironments();
  
  // Get API endpoints based on selected environment
  const apiEndpoints = getApiEndpoints(selectedEnvironment);
  const INSTANCE_DROPDOWN_API = apiEndpoints.instanceDropdown;
  const QUEUE_PERFORMANCE_API = apiEndpoints.queuePerformance;

  // Single agent form state
  const [queuePerformanceForm, setQueuePerformanceForm] = useState({
      instance_id: '',
  });

  // Handle environment change
  const handleEnvironmentChange = (e) => {
    const newEnvironment = e.target.value;
      setSelectedEnvironment(newEnvironment);
      saveSelectedEnvironment(newEnvironment);
      setQueues([]);
      setError(null);
  };

  const handleQueuePerformanceFormChange = (e) => {
    const { name, value } = e.target;
    
    setQueuePerformanceForm(prev => ({ ...prev, [name]: value }));
    if (name === 'instance_id') {
      setQueues([]);
      setError(null);
    }
  };


  const handleQueuePerformanceSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await fetchQueuePerformanceList(queuePerformanceForm.instance_id);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to acquire list of agents');
    } finally {
      setLoading(false);
    }
  };

  const fetchQueuePerformanceList = async (instanceId) => {
    if (!instanceId) {
      console.warn('[Queue Performance] Request skipped: instance_id is empty');
      setQueues([]);
      return;
    }

    setLoadingQueues(true);
    const payload = {
      instance_id: instanceId,
      include_zero_rows: true,
    };
    console.info('[Queue Performance] Request:', {
      endpoint: QUEUE_PERFORMANCE_API,
      payload,
    });

    try {
      const response = await apiService.post(QUEUE_PERFORMANCE_API, payload);
      console.info('[Queue Performance] Response:', {
        httpStatus: response.status,
        data: response.data,
      });

      if (response.data.status === 'ok') {
        const rows = response.data.result?.rows || [];
        console.info('[Queue Performance] Rows received:', rows.length, rows);
        setQueues(rows);
        return;
      }

      const message = response.data.message || response.data.result?.message || 'Unknown API error';
      console.error('[Queue Performance] API returned an error:', {
        message,
        data: response.data,
      });
      throw new Error(message);
    } catch (err) {
      console.error('[Queue Performance] Request failed:', {
        message: err.message,
        httpStatus: err.response?.status,
        responseData: err.response?.data,
        error: err,
      });
      throw err;
    } finally {
      setLoadingQueues(false);
    }
  };

   const formatSeconds = (value) => {
    if (value === null || value === undefined) return '-';
    const num = Number(value);
    if (Number.isNaN(num)) return '-';
    return num.toLocaleString();
  };

  const getSortValue = (queue, key) => {
    if (key === 'queue_name') return (queue.queue_name || '').toLowerCase();
    return Number(queue[key] || 0);
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((direction) => (direction === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const sortIndicator = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const filteredQueues = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return queues;
    return queues.filter((queue) => (
      (queue.queue_name || '').toLowerCase().includes(term) ||
      (queue.queue_id || '').toLowerCase().includes(term)
    ));
  }, [queues, searchTerm]);

  const sortedQueues = React.useMemo(() => {
    if (!sortKey) return filteredQueues;
    return [...filteredQueues].sort((left, right) => {
      const leftValue = getSortValue(left, sortKey);
      const rightValue = getSortValue(right, sortKey);
      if (leftValue === rightValue) return 0;
      const result = leftValue < rightValue ? -1 : 1;
      return sortDir === 'asc' ? result : -result;
    });
  }, [filteredQueues, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedQueues.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedQueues = sortedQueues.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Fetch Connect instances on component mount and when environment changes
  useEffect(() => {
    const fetchInstances = async () => {
      setLoadingInstances(true);
      try {
        const response = await apiService.get(INSTANCE_DROPDOWN_API);
        if (response.data.status === 'ok') {
          setInstances(response.data.instances || []);
        } else {
          console.error('Failed to load instances:', response.data.message);
        }
      } catch (err) {
        console.error('Error fetching instances:', err);
      } finally {
        setLoadingInstances(false);
      }
    };

    fetchInstances();
  }, [INSTANCE_DROPDOWN_API, selectedEnvironment]);

  return (
    <div className="setup-page">
        <div className="page-header">
            <div>
            <h1>Queue Performance Report</h1>
                <p className="page-subtitle">View Queue Performance</p>
            </div>
            <Link to="/reports" className="btn btn-secondary">
                ← Back to Reports
            </Link>
        </div>
    
        {/* Environment Selector */}
        <div className="environment-selector" style={{
            backgroundColor: '#f9f9f9',
            border: '2px solid #e0e0e0',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        }}>
        <label htmlFor="environment" style={{ 
            fontWeight: 'bold', 
            fontSize: '16px',
            minWidth: '120px'
        }}>
            Environment:
        </label>
        <select
            id="environment"
            value={selectedEnvironment}
            onChange={handleEnvironmentChange}
            style={{
            padding: '10px 16px',
            fontSize: '15px',
            borderRadius: '6px',
            border: '2px solid #ccc',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontWeight: '500',
            minWidth: '200px'
            }}
            >
            {availableEnvironments.map(env => (
            <option key={env} value={env}>
                {ENVIRONMENT_LABELS[env]} ({env})
            </option>
            ))}
            </select>
            <span style={{
            padding: '6px 12px',
            borderRadius: '4px',
            fontSize: '13px',
            fontWeight: 'bold',
            color: 'white',
            backgroundColor: getEnvironmentColor(selectedEnvironment)
            }}>
            {selectedEnvironment}
            </span>
        </div>

               <div className="form-container">
            <form onSubmit={handleQueuePerformanceSubmit} className="setup-form">
                <div className="form-section">
                <h3>Instance Information</h3>
                    <div className="form-group">
                        <label htmlFor="instance_id">Instance *</label>
                        <select
                        id="instance_id"
                        name="instance_id"
                        value={queuePerformanceForm.instance_id}
                        onChange={handleQueuePerformanceFormChange}
                        required
                        disabled={loadingInstances}
                        >
                        <option value="">
                            {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                        </option>
                        {instances.map((instance) => (
                            <option key={instance.id} value={instance.id}>
                            {instance.name} ({instance.status})
                            </option>
                        ))}
                        </select>
                        <small>Select an Amazon Connect instance</small>
                    </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading || loadingQueues || !queuePerformanceForm.instance_id}>
                    {loading || loadingQueues ? 'Viewing...' : 'View Performance'}
                  </button>
              </div>
            </form>
        </div>  

        {(loading || loadingQueues) && (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Processing your request...</p>
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {queues.length > 0 && (
          <div className="table-format-list">
            <div className="table-format-list-header">
              <h3>Queues ({sortedQueues.length}{sortedQueues.length !== queues.length ? ` of ${queues.length}` : ''})</h3>
              <div className="table-format-list-controls">
                <input
                  type="text"
                  className="table-format-search-input"
                  placeholder="Search queue name or ID..."
                  value={searchTerm}
                  onChange={(event) => { setSearchTerm(event.target.value); setPage(1); }}
                />
                <div className="page-size-selector">
                  <label htmlFor="page_size">Rows per page:</label>
                  <select id="page_size" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('queue_name')}>Queue{sortIndicator('queue_name')}</th>
                  <th className="sortable" onClick={() => handleSort('offered')}>Offered{sortIndicator('offered')}</th>
                  <th className="sortable" onClick={() => handleSort('handled')}>Handled{sortIndicator('handled')}</th>
                  <th className="sortable" onClick={() => handleSort('abandoned')}>Abandoned{sortIndicator('abandoned')}</th>
                  <th className="sortable" onClick={() => handleSort('abandoned_rate')}>Abandoned Rate{sortIndicator('abandoned_rate')}</th>
                  <th className="sortable" onClick={() => handleSort('average_wait_time')}>Ave Wait Time{sortIndicator('average_wait_time')}</th>
                  <th className="sortable" onClick={() => handleSort('max_wait_time')}>Max Wait Time{sortIndicator('max_wait_time')}</th>
                  <th className="sortable" onClick={() => handleSort('average_abandoned_time')}>Average Abandoned Time{sortIndicator('average_abandoned_time')}</th>
                </tr>
              </thead>
              <tbody>
                {pagedQueues.map((queue) => (
                  <tr key={queue.queue_id}>
                    <td>{queue.queue_name || '-'}</td>
                    <td>{queue.offered ?? '-'}</td>
                    <td>{queue.handled ?? '-'}</td>
                    <td>{queue.abandoned ?? '-'}</td>
                    <td>{queue.abandoned_rate ?? '-'}%</td>
                    <td>{formatSeconds(queue.average_wait_time)}</td>
                    <td>{formatSeconds(queue.max_wait_time)}</td>
                    <td>{formatSeconds(queue.average_abandoned_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination-controls">
              <button type="button" className="btn btn-secondary" onClick={() => setPage(1)} disabled={currentPage === 1}>« First</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={currentPage === 1}>‹ Prev</button>
              <span className="pagination-status">Page {currentPage} of {totalPages}</span>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((value) => Math.min(totalPages, value + 1))} disabled={currentPage === totalPages}>Next ›</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}>Last »</button>
            </div>
          </div>
        )}

        {!loading && queues.length === 0 && !error && (
          <div className="agent-list">
            <h3>Queues (0)</h3>
            <p>No queue performance data found.</p>
          </div>
        )}
    </div>
  );
};

export default QueuePerformanceReportPage;